/**
 * ActionButton — operator-verb-aware button primitive.
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Section C + Section M
 * React component contract.
 *
 * Every operator-facing action button across Phase 5+ surfaces routes through
 * this component. Picks label, icon, variant, and optional confirmation from
 * the OPERATOR_VERBS dictionary so anti-pattern labels can never sneak in.
 *
 * If the verb requires confirmation (per dictionary), pass `actionConfirmId`
 * and `confirmContext` to render an <ActionConfirm/> dialog from the help
 * registry before invoking `onClick`.
 *
 * Usage:
 *   <ActionButton verb="send_now" onClick={fn} />
 *   <ActionButton
 *     verb="delete"
 *     actionConfirmId="delete_template"
 *     confirmContext={{ trigger_count: 3, funnel_count: 1, future_send_count: 4237 }}
 *     onClick={handleDelete}
 *   />
 */

import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { Icon } from "@mui/material";
import { OperatorVerb, getVerb } from "../../constants/operatorVerbs";
import { ActionConfirm } from "../help";

export interface ActionButtonProps {
  /** Verb id from OPERATOR_VERBS — picks label/icon/variant/confirm. */
  verb: OperatorVerb;
  /** Click handler. For confirm-required verbs, called only after the user confirms. */
  onClick: () => void | Promise<void>;
  /**
   * For destructive/irreversible verbs, the actionConfirms id to render via
   * <ActionConfirm/>. If omitted on a verb that requires confirm, a simple
   * window.confirm fallback is used.
   */
  actionConfirmId?: string;
  /** Variables to interpolate into the confirm dialog copy. */
  confirmContext?: Record<string, string | number | undefined>;
  /** Disabled state. */
  disabled?: boolean;
  /** Loading state — disables and shows progress text. */
  loading?: boolean;
  /** Button size. Default "medium". */
  size?: "small" | "medium" | "large";
  /** Override variant from the verb dictionary. Rarely needed. */
  variantOverride?: "primary" | "secondary" | "destructive" | "ghost";
  /** Optional custom label (e.g. for context-specific phrasing). */
  labelOverride?: string;
  /** Whether to show the icon (default true). */
  showIcon?: boolean;
  /** Full width. */
  fullWidth?: boolean;
}

// ---------------------------------------------------------------------------
// MUI variant + sx mapping per visual variant
// ---------------------------------------------------------------------------
function getButtonStyles(variant: "primary" | "secondary" | "destructive" | "ghost") {
  switch (variant) {
    case "primary":
      return {
        muiVariant: "contained" as const,
        sx: {
          backgroundColor: "#D4AC0D", // Warm Gold
          color: "#1B1F23",
          "&:hover": { backgroundColor: "#B89309" },
          "&.Mui-disabled": { backgroundColor: "#E5E0D2", color: "#B4BCC5" },
        },
      };
    case "secondary":
      return {
        muiVariant: "outlined" as const,
        sx: {
          borderColor: "#1A5F7A",
          color: "#1A5F7A",
          "&:hover": {
            backgroundColor: "#F9F3E9",
            borderColor: "#1A5F7A",
          },
        },
      };
    case "destructive":
      return {
        muiVariant: "contained" as const,
        sx: {
          backgroundColor: "#7D1935",
          color: "#FFFFFF",
          "&:hover": { backgroundColor: "#5C0F26" },
          "&.Mui-disabled": { backgroundColor: "#E5E0D2", color: "#B4BCC5" },
        },
      };
    case "ghost":
    default:
      return {
        muiVariant: "text" as const,
        sx: {
          color: "#1A5F7A",
          "&:hover": { backgroundColor: "#F9F3E9" },
        },
      };
  }
}

const SIZE_TO_MIN_HEIGHT = {
  small: 36,
  medium: 44,
  large: 52, // 52pt tap target per V2 brand spec
};

export const ActionButton: React.FC<ActionButtonProps> = ({
  verb,
  onClick,
  actionConfirmId,
  confirmContext,
  disabled = false,
  loading = false,
  size = "medium",
  variantOverride,
  labelOverride,
  showIcon = true,
  fullWidth = false,
}) => {
  const config = getVerb(verb);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const variant = variantOverride ?? config.variant;
  const styles = getButtonStyles(variant);
  const minHeight = SIZE_TO_MIN_HEIGHT[size];

  const handleClick = async () => {
    if (config.requiresConfirm && actionConfirmId) {
      setConfirmOpen(true);
      return;
    }
    if (config.requiresConfirm && !actionConfirmId) {
      // Fallback for verbs that need confirm but no ActionConfirm id was
      // provided. Use native window.confirm so we never silently destruct.
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Confirm: ${config.label}?`)) return;
    }
    await onClick();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await onClick();
  };

  const buttonLabel = loading ? "Working…" : (labelOverride ?? config.label);

  const tooltip =
    config.shortcut ?
      `${config.intent}${config.shortcut ? `  (${config.shortcut})` : ""}` :
      config.intent;

  return (
    <>
      <Tooltip title={tooltip} placement="top" arrow enterDelay={400}>
        <span style={fullWidth ? { width: "100%" } : undefined}>
          <Button
            variant={styles.muiVariant}
            onClick={handleClick}
            disabled={disabled || loading}
            fullWidth={fullWidth}
            startIcon={
              showIcon ? <Icon sx={{ fontSize: size === "small" ? 16 : 20 }}>{config.icon}</Icon> : undefined
            }
            sx={{
              ...styles.sx,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              minHeight,
              minWidth: 44, // 44pt tap target
              px: size === "small" ? 1.5 : 2,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
            aria-label={config.intent}
          >
            {buttonLabel}
          </Button>
        </span>
      </Tooltip>

      {actionConfirmId && (
        <ActionConfirm
          actionId={actionConfirmId}
          open={confirmOpen}
          context={confirmContext}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
};

export default ActionButton;
